"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { CalendarDays, MapPin, Trophy, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const upcomingEvents = [{
  id: 1,
  dateStr: "2026-05-22",
  displayDate: "May 22 - 24, 2026",
  title: "Canadian Cavalier King Charles Spaniel National",
  location: "Chilliwack, BC",
  type: "National Specialty Show",
  description: "Join Mile High Cavaliers we judge SWEEPS at the Canadian National. A great way to see us judge our own breed.",
  link: "#"
}, {
  id: 2,
  dateStr: "2026-06-02",
  displayDate: "June, 2, 2026",
  title: "Flatirons Kennel Club",
  location: "Boulder County Fairgrounds, Longmont, CO",
  type: "All-Breed",
  description: "We will be presenting our newest generation of champions at this prestigious all-breed event.",
  link: "#"
}, {
  id: 3,
  dateStr: "2026-06-12",
  displayDate: "June 12 - 14, 2026",
  title: "Plum Creek Kennel Club",
  location: "Arapahoe County Fairgrounds, Aurora, CO",
  type: "All-Breed",
  description: "Come join us and fellow breeders, Intrynzik, Sheeba, Cobrnik and Saltayre Cavaliers - and watch us compete against some of the most stunning Cavaliers in the country.",
  link: "#"
}, {
  id: 4,
  dateStr: "2026-07-02",
  displayDate: "July 2 - 5, 2026",
  title: "CKCSC Cavalier of the West Specialty Show",
  location: "San Francisco, CA",
  type: "Cavalier Only Specialy Show",
  description: "Watch John Judge Sweeps and then Show our up-and-coming Cavaliers in Burlingame, CA!",
  link: "#"
}, {
  id: 5,
  dateStr: "2026-07-10",
  displayDate: "July 10 - 12, 2026",
  title: "Roaring Forks Kennel Club",
  location: "Castle Rock, CO",
  type: "All-Breed Show",
  description: "Join Mile High Cavaliers and meet us for lunch discussion about owning and care for CKCS!",
  link: "#"
}, {
  id: 6,
  dateStr: "2026-08-15",
  displayDate: "August 15 - 17, 2026",
  title: "Greeley Kennel Club",
  location: "Island Grove Park, Greeley, CO",
  type: "All-Breed",
  description: "We will be presenting our newest generation of champions at this prestigious all-breed event.",
  link: "#"
}, {
  id: 7,
  dateStr: "2026-09-03",
  displayDate: "September 2 - 6, 2026",
  title: "Metro Mile High Kennel Club (Toy Breeds Only) and Evergreen Kennel Club",
  location: "Island Grove Park, Greeley, CO",
  type: "2 Toy Breed Shows and 3 All-Breed Show",
  description: "Come join us and fellow breeders, Intrynzik, Sheeba, Cobrnik - and watch us compete against some of the most stunning Cavaliers in the country.",
  link: "#"
}, {
  id: 9,
  dateStr: "2026-09-26",
  displayDate: "September 26, 2026",
  title: "FUN MATCH! Cavaliers of The West",
  location: "Centennial, CO",
  type: "Cavaliers Only FUN MATCH!",
  description: "Join us for food, fun and education on our beloved Cavaliers. Practice showing (don't worry we will teach you) with one of our National Judges and have lunch and learn from our Veteranarians and breed experts on all things Cavalier.",
  link: "#"
}, {
  id: 8,
  dateStr: "2026-09-18",
  displayDate: "September 18 - 20, 2026",
  title: "Arapahoe Kennel Club",
  location: "Arapahoe County Fairgrounds, Aurora, CO",
  type: "All-Breed Show",
  description: "Join Mile High Cavaliers practically in our backyard. We show our new puppies and our National Champions at this show!",
  link: "#"
}, {
  id: 10,
  dateStr: "2026-10-08",
  displayDate: "October 8 - 11, 2026",
  title: "Cavalier King Charles Spaniel Club, USA National",
  location: "Louisville, KY",
  type: "CKCSC National Specialty",
  description: "Some of the best Cavaliers in the Country. This is the Superbowl of Cavaliers Dog Show!",
  link: "#"
}, {
  id: 11,
  dateStr: "2026-10-23",
  displayDate: "October 23 - 25, 2026",
  title: "Colorado Springs Kennel Club",
  location: "Colorado Springs, CO",
  type: "All-Breed",
  description: "Come see us show in Colorado Springs, Colorado!",
  link: "#"
}];
export default function ShowCalendar() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [date, setDate] = useState<Date | undefined>(new Date());
  return <section id="show-calendar" ref={ref} className={cn("py-32 px-4 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-serif mb-6 text-foreground">Show Calendar</h2>
          <div className="w-24 h-1 bg-primary mx-auto mb-8" />
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">Experience the fun of Mile High Cavaliers and our dogs in person at our upcoming specialty shows and competitions across the country.</p>
        </div>

        <Card className="grid grid-cols-1 lg:grid-cols-12 overflow-hidden border-none shadow-2xl bg-card gap-0">
          <div className="lg:col-span-5 p-8 bg-accent/20 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-border">
            <div className="mb-8 text-center">
              <h3 className="text-xl font-bold font-serif mb-2">Event Schedule</h3>
              <p className="text-sm text-muted-foreground">Select a date to view regional activities</p>
            </div>
            <SafeHydrate><Calendar mode="single" selected={date} onSelect={setDate} className="rounded-none border-none p-0" /></SafeHydrate>
            <div className="mt-8 flex items-center gap-3 text-sm text-muted-foreground italic">
              <Trophy className="size-4 text-primary" />
              <span>Come see us AFTER we show!</span>
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col h-[600px]">
            <CardHeader className="border-b border-border/50 py-6">
              <div className="flex items-center gap-3">
                <CalendarDays className="text-primary size-6" />
                <div>
                  <CardTitle className="text-2xl font-serif">Scheduled Shows</CardTitle>
                  <CardDescription>Our planned specialty shows and national competitions</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-8 space-y-10">
                  {upcomingEvents.map((event, i) => <div key={event.id} data-index={i} className="group relative">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                        <div className="space-y-4 flex-1">
                          <div className="flex flex-wrap items-center gap-3">
                            <Badge variant="secondary" className="bg-secondary/10 text-secondary border-secondary/20 rounded-none uppercase tracking-widest text-[10px] font-bold px-3 py-1">
                              {event.type}
                            </Badge>
                            <span className="text-sm font-medium text-primary">
                              {event.displayDate}
                            </span>
                          </div>
                          
                          <h4 className="text-2xl font-bold text-foreground leading-tight group-hover:text-primary transition-colors">
                            {event.title}
                          </h4>
                          
                          <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            <MapPin className="size-4 text-primary" />
                            <span>{event.location}</span>
                          </div>
                          
                          <p className="text-muted-foreground leading-relaxed text-base">
                            {event.description}
                          </p>
                        </div>
                        
                        <div className="flex items-start pt-2">
                          <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground rounded-none px-6 transition-all duration-300">
                            
                          </Button>
                        </div>
                      </div>
                      {i < upcomingEvents.length - 1 && <Separator className="mt-10 bg-border/60" />}
                    </div>)}
                </div>
              </ScrollArea>
            </CardContent>
          </div>
        </Card>
      </div>
    </section>;
}